import React from "react";
import dayjs from "dayjs";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "ag-grid-community/styles/ag-grid.css";
import { useState, useMemo, useEffect, useRef } from "react";
import PulseLoader from "../Utility/PulseLoader";
import CustomHeader from "../Utility/CustomHeader";
import { FiDownload, FiSearch } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { Whisper } from "rsuite";
import ActionCellStaff from "../Utility/ActionCellStaff";

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

function StaffListTable({ data = [], loading }) {
  const [searchTerm, setSearchTerm] = useState("");
  const gridRef = useRef(null);
  const [rowData, setRowData] = useState([]);
  const navigate = useNavigate();

  // Custom loading overlay
  const CustomLoadingOverlay = () => (
    <div className="flex flex-col items-center justify-center h-full">
      <PulseLoader />
    </div>
  );

  // Custom no-rows overlay
  const CustomNoRowsOverlay = () => (
    <div className="flex flex-col items-center justify-center h-full">
      <img
        src="/Images/doctortable.png"
        alt="No staff available"
        className="w-64 h-64 object-contain mb-6"
      />
      <p className="text-gray-600 text-lg font-semibold">No Staff Available</p>
    </div>
  );

  // Styles (updated for staff table, visually similar to original)
  const tableStyle = `
    .focus\\:ring-\\[\\#00C335\\]:focus {
      --tw-ring-opacity: 1;
      --tw-ring-color: rgb(21 44 64 / 68%);
    }
    .ag-theme-quartz .ag-row-selected::before,
    .ag-theme-quartz .ag-row-selected::after {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background-color: #77c658;
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
      z-index: 1;
    }
    .ag-row-odd {
      background-color: #fff !important;
    }
    .ag-row-even {
      background-color: #f7f7f7 !important;
    }
    .ag-header {
      background-color: #A2AD91;
      border-bottom: var(--ag-borders-critical) var(--ag-border-color);
    }
    .ag-header-cell-text {
      font-size: 0.9rem;
      text-transform: uppercase;
      color: #1e374f;
      font-weight: 600;
      font-family: 'Nunito', sans-serif !important;
    }
    .ag-ltr .ag-cell {
      font-weight: 500 !important;
      letter-spacing: 0.5px;
      color: #2b2b2b !important;
      text-align: center;
      display: flex;
      align-items: center;
    }
    .ag-cell, .ag-header-cell-label {
      font-size: .9rem;
    }
    .span-active {
      color: #f9fcfcff;
      background: #2C994B;
      border-radius: 2rem;
      font-size: 13px;
      padding: .2rem .85rem;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    .span-inactive {
      color: #6B7280;
      background: #c7c7c9ff;
      border-radius: 2rem;
      font-size: 13px;
      padding: .2rem .85rem;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    .span-onleave {
      color: #f9fcfcff;
      background: #e93832ff;
      border-radius: 2rem;
      font-size: 13px;
      padding: .2rem .85rem;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
  `;

  useEffect(() => {
    if (!gridRef.current || !gridRef.current.api) return;
    const api = gridRef.current.api;
    if (loading) {
      api.showLoadingOverlay();
    } else if (!loading && rowData.length === 0) {
      api.showNoRowsOverlay();
    } else {
      api.hideOverlay();
    }
  }, [loading, rowData]);

  useEffect(() => {
    setRowData(data);
  }, [data]);

  // Generates colored avatar circle based on staff name
  const getStableColor = (key) => {
    const bgColors = ["bg-warning bg-opacity-50", "bg-primary bg-opacity-50", "bg-success bg-opacity-50", "bg-danger bg-opacity-50"];
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      hash = key.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % bgColors.length;
    return bgColors[index];
  };

  // Table columns for staff
  const [colDefs, setColDef] = useState([
    {
      headerName: "Name",
      field: "name",
      minWidth: 190,
      cellRenderer: function (params) {
        const name = params.value || "U";
        const initial = name.charAt(0).toUpperCase();
        const bgColor = getStableColor(name);
        return (
          <div className="d-flex align-items-center gap-2">
            <div
              className={`d-flex justify-content-center align-items-center rounded-circle text-white fw-bold ${bgColor}`}
              style={{ width: "2rem", height: "2rem", fontSize: "0.875rem" }}
            >
              {initial}
            </div>
            <span className="fw-bold text-secondary small">{name}</span>
          </div>
        );
      },
    },
    {
      headerName: "Role",
      field: "role",
      minWidth: 148,
      cellRenderer: params => <span className="text-primary fw-semibold small">{params.value}</span>,
    },
    {
      headerName: "Department",
      field: "department",
      minWidth: 120,
      cellRenderer: params => <span className="text-secondary fw-medium small">{params.value}</span>,
    },
    {
      headerName: "Contact",
      field: "contact",
      minWidth: 210,
      cellRenderer: params => {
        const email = params.value;
        const phone = params.data.phone;
        return (
          <div className="flex flex-col items-start">
            <span className="text-muted text-sm">{email}</span>
            <span className="text-muted text-xs">{phone}</span>
          </div>
        );
      },
    },
    {
      headerName: "Joined",
      field: "joined",
      minWidth: 138,
      cellRenderer: params => {
        const formattedDate = dayjs(params.value).format("DD-MMM-YYYY");
        return <span className="text-secondary fw-medium small">{formattedDate}</span>;
      },
    },
    {
      headerName: "Status",
      field: "status",
      minWidth: 125,
      cellRenderer: (params) => {
        const value = params.value;
        let className = "span-active";
        if (value === "Inactive") className = "span-inactive";
        else if (value === "On Leave") className = "span-onleave";
        return (
          <span className={className} style={{ textAlign: "center" }}>
            {value}
          </span>
        );
      },
    },
    {
      headerName: 'Actions',
      field: 'Actions',
      minWidth: 60,
      cellRenderer: ActionCellStaff,
    },
  ]);

  const defaultColDef = useMemo(() => ({
    flex: 1,
    filter: true,
    sortable: true,
    enableRowGroup: true,
    sort: "asc",
    resizable: true,
    headerComponent: CustomHeader,
  }), []);

  // Optional: adjust autosize and pagination if needed
  const autoSizeStrategy = {
    type: "fitGridWidth",
    columnLimits: [
      { colId: "Name", minWidth: 150 },
    ],
  };
  const paginationPageSizeSelector = useMemo(() => [10, 20, 50, 100], []);

  return (
    <div className="p-2 border rounded-lg bg-white shadow-sm mb-5">
      <div style={{ width: "100%", height: "100%" }}>
        <div
          id="grid-wrapper"
          className="ag-theme-quartz custom-ag-grid"
          style={{
            width: "100%",
            height: "454px",
            boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
            borderRadius: "12px",
            backgroundColor: "#fff",
            minHeight: "32rem",
          }}>
          <style>{tableStyle}</style>
          <AgGridReact
            rowData={rowData}
            columnDefs={colDefs}
            defaultColDef={defaultColDef}
            rowClass="tbl-row-sc"
            ref={gridRef}
            loadingOverlayComponent={CustomLoadingOverlay}
            noRowsOverlayComponent={CustomNoRowsOverlay}
            rowHeight={40}
            headerHeight={30}
            autoSizeStrategy={autoSizeStrategy}
          />
        </div>
      </div>
    </div>
  );
}

export default StaffListTable;
